import { Component, DestroyRef, computed, inject, input, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { ThumbnailsCarouselComponent } from '@app/shared/components/carousel/thumbnails-carousel/thumbnails-carousel.component';
import { MenuActionDirective } from '@app/shared/directives/menu-action.directive';
import { ActiveProjectService } from '@app/shared/services/active-project.service';
import { FileDataService } from '@app/shared/services/file-data.service';
import { LocationsActionsService } from '@app/shared/services/locations-actions.service';
import { ToastService } from '@app/shared/services/shared/toast.service';
import { FileContext } from '@app/shared/types/enums/file-enums';
import { FileInfo } from '@app/shared/types/models/files/file-info';
import { Location } from '@app/shared/types/models/location/location.model';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { FileDropComponent } from '../files/file-drop/file-drop.component';

/**
 * Row body for a project location inside `proffeo-ui-sortable-tree`.
 * Expand, drag, and selection chrome live on the tree; this component owns label, files, and actions.
 */
@Component({
  selector: 'proffeo-project-location-item',
  imports: [
    MenuActionDirective,
    MatButtonModule,
    MatIconModule,
    TranslatePipe,
    MatMenuModule,
    ThumbnailsCarouselComponent,
    FileDropComponent
  ],
  templateUrl: './project-location-item.component.html',
  styleUrl: './project-location-item.component.scss'
})
export class ProjectLocationItemComponent {
  private readonly toastService = inject(ToastService);
  private readonly translateService = inject(TranslateService);
  private readonly filesService = inject(FileDataService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly locationsActionsService = inject(LocationsActionsService);
  private readonly activeProjectService = inject(ActiveProjectService);
  private readonly projectId = this.activeProjectService.activeProjectId;
  private readonly editableProjectLocations = this.locationsActionsService.getEditableProjectLocations();

  protected readonly filesVisible = signal(false);
  protected readonly locationFiles = computed(() =>
    this.filesService.getFiles({
      context: FileContext.locationFile,
      projectId: this.projectId(),
      locationId: this.location().id
    })()
  );

  public readonly location = input.required<Location>();

  protected toggleFile(event: Event): void {
    event.stopPropagation();
    this.filesVisible.update(visible => !visible);
  }

  protected openEditLocationDialog(location: Location): void {
    this.locationsActionsService.openEditLocationDialog(location);
  }

  protected openAddLocationDialog(location: Location): void {
    this.locationsActionsService.openAddLocationDialog(location);
  }

  protected openAddMultipleLocationDialog(location: Location): void {
    this.locationsActionsService.openAddMultipleLocationDialog(location);
  }

  protected openChangeProjectLocationParentDialog(): void {
    this.locationsActionsService.openChangeLocationParentDialog({
      location: this.location(),
      locations: this.editableProjectLocations
    });
  }

  protected openDuplicateProjectLocationDialog(): void {
    this.locationsActionsService.openDuplicateLocationDialog({
      locationsToDuplicate: [this.location()],
      locations: this.editableProjectLocations
    });
  }

  protected openProjectLocationDeleteConfirmationDialog(location: Location): void {
    this.locationsActionsService
      .openDeleteConfirmationDialog(location)
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((confirmed: boolean) => {
        if (confirmed) {
          this.deleteLocation(location);
        }
      });
  }

  protected deleteLocation(location: Location): void {
    this.locationsActionsService
      .deleteLocations([location])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() =>
        this.toastService.success(
          this.translateService.instant('project-locations.toasts.successfully-deleted', { name: location.name })
        )
      );
  }

  protected onThumbnailClick(index: number): void {
    this.locationsActionsService.openCarouselWithLocationFiles(this.location, this.locationFiles, index);
  }

  protected onFilesAdded(files: File[]): void {
    this.locationsActionsService.addFiles(this.location, files, this.locationFiles().length);
  }

  protected onFileDelete(file: FileInfo): void {
    this.locationsActionsService.deleteFile(this.location, file);
  }
}
